import React, { useEffect, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTrip } from '../contexts/TripContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { MemberDetailsModal } from '../components/trip/MemberDetailsModal';
import { AttendanceGrid } from '../components/trip/AttendanceGrid';
import { CostSummary } from '../components/trip/CostSummary';
import { LogisticsReport } from '../components/trip/LogisticsReport';
import { WalletCard } from '../components/wallet/WalletCard';
import { WalletModal, WalletTransferModal } from '../components/wallet/WalletModals';
import { copyToClipboard } from '../utils/clipboard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Crown, CheckCircle2, Clock, Users, Eye, UserPlus, UserMinus, 
  Lock, Unlock, AlertCircle, X, Check, Loader2, Calendar, DollarSign, Package, Wallet, Send, Plane, Mail, Phone, RefreshCw, Share2, MessageSquare, Link2, Copy, Plus, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { PageHeader } from '../components/common/PageHeader';
import TransportSummary from '../components/trip/TransportSummary';

export default function TripMembers() {
  const { tripId } = useParams();
  const context = useOutletContext();
  const { trip, members, isOrganizer } = context || {};
  const { fetchMembers, approveMember, rejectMember, closeRegistrations, openRegistrations } = useTrip();
  const { user, api } = useAuth();
  const { t, language } = useLanguage();
  const dateLocale = language === 'en-US' ? enUS : ptBR;

  const roleConfig = {
    organizer: { label: t('members.organizer'), color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
    traveler: { label: t('members.traveler'), color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
  };

  const statusConfig = {
    confirmed: { label: t('members.confirmed'), icon: CheckCircle2, color: 'text-green-500' },
    pending: { label: t('members.waitingApproval'), icon: Clock, color: 'text-yellow-500' },
    declined: { label: t('members.declined'), icon: X, color: 'text-red-500' },
  };
  
  // Modal state
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [loadingAction, setLoadingAction] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Wallet state
  const [wallets, setWallets] = useState([]);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [selectedWalletUser, setSelectedWalletUser] = useState({ id: null, name: null });
  const [walletsLoading, setWalletsLoading] = useState(false);

  // Pending invites state
  const [pendingInvites, setPendingInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(null);

  // Resend invite modal state
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [resendModalInvite, setResendModalInvite] = useState(null);
  const [inviteMessage, setInviteMessage] = useState('');
  const [copied, setCopied] = useState(false);

  // Currency formatting
  const formatCurrency = (value) => {
    const currency = trip?.currency || 'BRL';
    return new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
      style: 'currency',
      currency: currency,
    }).format(value || 0);
  };

  // Fetch wallets
  const fetchWallets = async () => {
    if (!tripId) return;
    setWalletsLoading(true);
    try {
      const response = await api.get(`/trips/${tripId}/wallets`);
      setWallets(response.data || []);
    } catch (error) {
      console.error('Error fetching wallets:', error);
    } finally {
      setWalletsLoading(false);
    }
  };

  // Fetch pending invites
  const fetchPendingInvites = async () => {
    if (!tripId) return;
    setInvitesLoading(true);
    try {
      const response = await api.get(`/trips/${tripId}/pending-invites`);
      setPendingInvites(response.data?.invites || []);
    } catch (error) {
      console.error('Error fetching pending invites:', error);
    } finally {
      setInvitesLoading(false);
    }
  };

  // Resend invite
  const handleResendInvite = async (contact) => {
    setResendingInvite(contact);
    try {
      const response = await api.post(`/trips/${tripId}/resend-invite/${encodeURIComponent(contact)}`);
      
      if (response.data.status === 'sent') {
        toast.success('Convite reenviado com sucesso!');
      } else if (response.data.status === 'manual' && response.data.whatsapp_url) {
        // Open WhatsApp link
        window.open(response.data.whatsapp_url, '_blank');
        toast.success('Link do WhatsApp aberto');
      } else {
        toast.error(response.data.message || 'Erro ao reenviar');
      }
      
      // Refresh invites list
      await fetchPendingInvites();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao reenviar convite';
      toast.error(message);
    } finally {
      setResendingInvite(null);
    }
  };

  // Check if can resend email (1 hour cooldown)
  const canResendEmail = (invite) => {
    if (invite.contact_type !== 'email') return true;
    if (!invite.last_sent_at) return true;
    
    const lastSent = new Date(invite.last_sent_at);
    const now = new Date();
    const diffMinutes = (now - lastSent) / (1000 * 60);
    return diffMinutes >= 60;
  };

  // Get remaining time for email cooldown
  const getEmailCooldownMinutes = (invite) => {
    if (!invite.last_sent_at) return 0;
    const lastSent = new Date(invite.last_sent_at);
    const now = new Date();
    const diffMinutes = (now - lastSent) / (1000 * 60);
    return Math.max(0, Math.ceil(60 - diffMinutes));
  };

  // Public URL for invite links
  const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || 'https://app.viajejunto.com.br';

  // Generate WhatsApp share link for invite
  const getWhatsAppShareLink = (invite) => {
    const inviteCode = trip?.invite_code || '';
    const inviteLink = `${PUBLIC_URL}/dashboard?join=${inviteCode}`;
    const message = `Olá ${invite?.name || ''}! Você está convidado para a viagem "${trip?.name}". Entre pelo link: ${inviteLink}`;
    
    if (invite?.contact_type === 'phone') {
      // If we have a phone number, send directly to that number
      const phone = invite.contact.replace(/\D/g, '');
      return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    }
    // Generic WhatsApp share
    return `https://wa.me/?text=${encodeURIComponent(message)}`;
  };

  // Copy invite link to clipboard
  const handleCopyInviteLink = async () => {
    const inviteCode = trip?.invite_code || '';
    const inviteLink = `${PUBLIC_URL}/dashboard?join=${inviteCode}`;
    const success = await copyToClipboard(inviteLink);
    if (success) {
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Erro ao copiar link');
    }
  };

  // Copy invite code to clipboard
  const handleCopyInviteCode = async () => {
    const inviteCode = trip?.invite_code || '';
    const success = await copyToClipboard(inviteCode);
    if (success) {
      toast.success('Código copiado!');
    } else {
      toast.error('Erro ao copiar código');
    }
  };

  // Open resend modal for a specific invite
  const openResendModal = (invite) => {
    setResendModalInvite(invite);
    setInviteMessage('');
    setResendModalOpen(true);
  };

  // Handle sending email from modal
  const handleSendEmailFromModal = async () => {
    if (!resendModalInvite) return;
    
    setResendingInvite(resendModalInvite.contact);
    try {
      const response = await api.post(`/trips/${tripId}/resend-invite/${encodeURIComponent(resendModalInvite.contact)}`);
      
      if (response.data.status === 'sent') {
        toast.success('Convite reenviado com sucesso!');
        setResendModalOpen(false);
      } else {
        toast.error(response.data.message || 'Erro ao reenviar');
      }
      
      // Refresh invites list
      await fetchPendingInvites();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao reenviar convite';
      toast.error(message);
    } finally {
      setResendingInvite(null);
    }
  };

  // Refresh all data on the page
  const refreshAllData = async () => {
    await fetchMembers(tripId);
    await fetchWallets();
    await fetchPendingInvites();
    setRefreshKey(prev => prev + 1); // Force re-render of child components
  };

  // Wallet handlers
  const handleViewWallet = (userId, userName) => {
    setSelectedWalletUser({ id: userId, name: userName });
    setWalletModalOpen(true);
  };

  const handleOpenTransfer = (fromUserId) => {
    setSelectedWalletUser({ id: fromUserId, name: null });
    setTransferModalOpen(true);
  };

  useEffect(() => {
    if (tripId) {
      fetchMembers(tripId);
      fetchWallets();
      fetchPendingInvites();
    }
  }, [tripId, fetchMembers]);

  // Filter members by status
  const confirmedOrganizers = members?.filter(m => m.role === 'organizer' && m.status === 'confirmed') || [];
  const confirmedTravelers = members?.filter(m => m.role === 'traveler' && m.status === 'confirmed') || [];
  const pendingMembers = members?.filter(m => m.status === 'pending') || [];

  const handleViewDetails = (member) => {
    if (member.status === 'confirmed') {
      setSelectedMember(member);
      setDetailsModalOpen(true);
    }
  };

  const handleApprove = async (member) => {
    setLoadingAction(member.id);
    try {
      await approveMember(tripId, member.id);
      toast.success(t('members.approved', { name: member.user_name }));
      // Refresh all data on the page
      await refreshAllData();
    } catch (error) {
      toast.error(t('errors.approving'));
    } finally {
      setLoadingAction(null);
    }
  };

  const handleReject = async (member) => {
    setLoadingAction(member.id);
    try {
      await rejectMember(tripId, member.id);
      toast.success(t('members.rejected'));
      // Refresh all data on the page
      await refreshAllData();
    } catch (error) {
      toast.error(t('errors.rejecting'));
    } finally {
      setLoadingAction(null);
    }
  };

  const handleToggleRegistrations = async () => {
    try {
      if (trip?.is_closed) {
        await openRegistrations(tripId);
        toast.success(t('members.tripOpened'));
      } else {
        await closeRegistrations(tripId);
        toast.success(t('members.tripClosed'));
      }
    } catch (error) {
      toast.error(t('errors.changingSettings'));
    }
  };

  const confirmedCount = confirmedOrganizers.length + confirmedTravelers.length;
  
  // Check if trip is in voting mode
  const isVotingMode = trip?.status === 'voting';
  
  // Calculate voting stats
  const votingStats = React.useMemo(() => {
    if (!isVotingMode || !trip?.destination_voting?.options) {
      return { totalVoters: 0, totalMembers: confirmedCount };
    }
    
    // Get unique voters across all options
    const allVoters = new Set();
    trip.destination_voting.options.forEach(option => {
      (option.votes || []).forEach(voterId => allVoters.add(voterId));
    });
    
    return {
      totalVoters: allVoters.size,
      totalMembers: confirmedCount
    };
  }, [isVotingMode, trip?.destination_voting?.options, confirmedCount]);
  
  // Determine subtitle based on mode
  const subtitleText = isVotingMode
    ? `${votingStats.totalVoters} de ${votingStats.totalMembers} ${votingStats.totalMembers === 1 ? 'membro votou' : 'membros votaram'}`
    : confirmedCount === 1 
      ? t('members.personConfirmed')
      : t('members.peopleConfirmed', { count: confirmedCount });

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={t('trips.members')}
        subtitle={subtitleText}
        helpScreen="members"
      />

      {/* Registration Control - Organizer Only */}
      {isOrganizer && (
        <Card className="border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {trip?.is_closed ? (
                  <Lock className="w-5 h-5 text-red-500" />
                ) : (
                  <Unlock className="w-5 h-5 text-green-500" />
                )}
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {trip?.is_closed ? t('members.tripClosedTitle') : t('members.tripOpenTitle')}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {trip?.is_closed 
                      ? t('members.tripClosedDesc')
                      : t('members.tripOpenDesc')
                    }
                  </p>
                </div>
              </div>
              <Switch
                checked={!trip?.is_closed}
                onCheckedChange={handleToggleRegistrations}
                data-testid="toggle-registrations"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Requests - Organizer Only */}
      {isOrganizer && pendingMembers.length > 0 && (
        <div>
          <h3 className="font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-yellow-500" />
            {t('members.pendingRequests')}
            <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
              {pendingMembers.length}
            </Badge>
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pendingMembers.map((member, index) => (
              <PendingMemberCard
                key={member.id}
                member={member}
                index={index}
                onApprove={() => handleApprove(member)}
                onReject={() => handleReject(member)}
                isLoading={loadingAction === member.id}
                t={t}
                dateLocale={dateLocale}
              />
            ))}
          </div>
        </div>
      )}

      {/* Pending Invites Section */}
      {pendingInvites.length > 0 && (
        <div>
          <h3 className="font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
            <Send className="w-4 h-4 text-blue-500" />
            Convites Enviados
            <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
              {pendingInvites.filter(i => i.status === 'pending').length} pendente{pendingInvites.filter(i => i.status === 'pending').length !== 1 ? 's' : ''}
            </Badge>
          </h3>
          <Card>
            <CardContent className="p-0 divide-y divide-slate-100 dark:divide-slate-800">
              {pendingInvites.map((invite, index) => (
                <motion.div
                  key={invite.contact}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-3 flex items-center gap-3"
                >
                  {/* Avatar/Icon */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    invite.status === 'joined' 
                      ? 'bg-green-100 dark:bg-green-900/30' 
                      : 'bg-slate-100 dark:bg-slate-800'
                  }`}>
                    {invite.status === 'joined' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : invite.contact_type === 'email' ? (
                      <Mail className="w-5 h-5 text-slate-400" />
                    ) : (
                      <Phone className="w-5 h-5 text-slate-400" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900 dark:text-white truncate">
                        {invite.name || 'Sem nome'}
                      </p>
                      {invite.status === 'joined' && (
                        <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs">
                          Entrou
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                      {invite.contact}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-slate-400">
                        {invite.send_count} envio{invite.send_count !== 1 ? 's' : ''}
                      </span>
                      {invite.last_sent_at && (
                        <span className="text-xs text-slate-400">
                          · Último: {formatDistanceToNow(new Date(invite.last_sent_at), { addSuffix: true, locale: dateLocale })}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action */}
                  {invite.status === 'pending' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openResendModal(invite)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
                    >
                      <Share2 className="w-4 h-4" />
                      <span className="ml-1 hidden sm:inline">Compartilhar</span>
                    </Button>
                  )}
                </motion.div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Organizers */}
      {confirmedOrganizers.length > 0 && (
        <div>
          <h3 className="font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
            <Crown className="w-4 h-4 text-orange-500" />
            {t('members.organizers')}
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {confirmedOrganizers.map((member, index) => (
              <MemberCard 
                key={member.id} 
                member={member} 
                index={index}
                isCurrentUser={member.user_id === user?.id}
                isOrganizer={isOrganizer}
                onViewDetails={() => handleViewDetails(member)}
                roleConfig={roleConfig}
                statusConfig={statusConfig}
                t={t}
              />
            ))}
          </div>
        </div>
      )}

      {/* Confirmed Travelers */}
      {confirmedTravelers.length > 0 && (
        <div>
          <h3 className="font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-sky-500" />
            {t('members.travelers')}
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {confirmedTravelers.map((member, index) => (
              <MemberCard 
                key={member.id} 
                member={member} 
                index={index}
                isCurrentUser={member.user_id === user?.id}
                isOrganizer={isOrganizer}
                onViewDetails={() => handleViewDetails(member)}
                roleConfig={roleConfig}
                statusConfig={statusConfig}
                t={t}
              />
            ))}
          </div>
        </div>
      )}

      {/* Attendance Grid - Shows daily presence - Only for confirmed trips (not voting mode) */}
      {confirmedCount > 0 && trip?.start_date && trip?.end_date && trip?.status !== 'voting' && (
        <div className="pt-4 space-y-6">
          <Tabs defaultValue="attendance" className="w-full">
            <TabsList className="grid w-full grid-cols-5 mb-4 h-auto p-1">
              <TabsTrigger value="attendance" className="flex flex-col items-center gap-1 py-2 px-1 text-[10px] sm:text-sm sm:flex-row sm:gap-2" data-testid="tab-attendance">
                <Calendar className="w-4 h-4" />
                <span>Presença</span>
              </TabsTrigger>
              <TabsTrigger value="transport" className="flex flex-col items-center gap-1 py-2 px-1 text-[10px] sm:text-sm sm:flex-row sm:gap-2" data-testid="tab-transport">
                <Plane className="w-4 h-4" />
                <span>Transporte</span>
              </TabsTrigger>
              <TabsTrigger value="wallets" className="flex flex-col items-center gap-1 py-2 px-1 text-[10px] sm:text-sm sm:flex-row sm:gap-2" data-testid="tab-wallets">
                <Wallet className="w-4 h-4" />
                <span>Carteiras</span>
              </TabsTrigger>
              <TabsTrigger value="costs" className="flex flex-col items-center gap-1 py-2 px-1 text-[10px] sm:text-sm sm:flex-row sm:gap-2" data-testid="tab-costs">
                <DollarSign className="w-4 h-4" />
                <span>Custos</span>
              </TabsTrigger>
              {isOrganizer && (
                <TabsTrigger value="logistics" className="flex flex-col items-center gap-1 py-2 px-1 text-[10px] sm:text-sm sm:flex-row sm:gap-2" data-testid="tab-logistics">
                  <Package className="w-4 h-4" />
                  <span>Logística</span>
                </TabsTrigger>
              )}
            </TabsList>
            
            <TabsContent value="attendance">
              <AttendanceGrid 
                key={`attendance-${refreshKey}`}
                tripId={tripId} 
                isOrganizer={isOrganizer}
              />
            </TabsContent>

            <TabsContent value="transport">
              <TransportSummary 
                key={`transport-${refreshKey}`}
                tripId={tripId}
              />
            </TabsContent>

            <TabsContent value="wallets">
              <div className="space-y-4">
                {/* Header with transfer button */}
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-slate-400 flex items-center gap-2">
                    <Wallet className="w-4 h-4" />
                    Carteiras dos Membros
                  </h3>
                  {isOrganizer && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTransferModalOpen(true)}
                      className="text-xs"
                    >
                      <Send className="w-3 h-3 mr-1" />
                      Transferir Saldo
                    </Button>
                  )}
                </div>

                {/* Wallets Grid */}
                {walletsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-orange-400" />
                  </div>
                ) : wallets.length === 0 ? (
                  <Card className="border-slate-700 border-dashed">
                    <CardContent className="py-8 text-center">
                      <Wallet className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                      <p className="text-slate-500">Nenhuma carteira encontrada</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2">
                    {wallets.map((wallet) => (
                      <WalletCard
                        key={wallet.user_id}
                        userId={wallet.user_id}
                        userName={wallet.user_name}
                        balance={wallet.balance}
                        totalCredits={wallet.total_credits}
                        totalDebits={wallet.total_debits}
                        formatCurrency={formatCurrency}
                        onViewExtract={handleViewWallet}
                        onTransfer={isOrganizer ? handleOpenTransfer : undefined}
                        isOwnWallet={wallet.user_id === user?.id}
                      />
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="costs">
              <CostSummary 
                key={`costs-${refreshKey}`}
                tripId={tripId}
                compact={!isOrganizer}
              />
            </TabsContent>
            
            {isOrganizer && (
              <TabsContent value="logistics">
                <LogisticsReport key={`logistics-${refreshKey}`} tripId={tripId} />
              </TabsContent>
            )}
          </Tabs>
        </div>
      )}

      {/* Empty state */}
      {(!members || members.length === 0) && (
        <Card className="border-slate-100 dark:border-slate-700 border-dashed">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-2">
              {t('members.noParticipants')}
            </h3>
            <p className="text-slate-500 dark:text-slate-400">
              {t('members.inviteHint')}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Member Details Modal */}
      {selectedMember && (
        <MemberDetailsModal
          tripId={tripId}
          memberId={selectedMember.user_id}
          memberName={selectedMember.user_name}
          isOpen={detailsModalOpen}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedMember(null);
          }}
        />
      )}

      {/* Wallet Modal */}
      <WalletModal
        open={walletModalOpen}
        onOpenChange={setWalletModalOpen}
        tripId={tripId}
        userId={selectedWalletUser.id}
        userName={selectedWalletUser.name}
        isOwnWallet={selectedWalletUser.id === user?.id}
        formatCurrency={formatCurrency}
      />

      {/* Transfer Modal */}
      <WalletTransferModal
        open={transferModalOpen}
        onOpenChange={setTransferModalOpen}
        tripId={tripId}
        members={members}
        currentUserId={user?.id}
        formatCurrency={formatCurrency}
        onTransferComplete={fetchWallets}
      />

      {/* Resend Invite Modal */}
      <Dialog open={resendModalOpen} onOpenChange={setResendModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-blue-500" />
              Compartilhar Convite
            </DialogTitle>
            <DialogDescription>
              Reenvie o convite para {resendModalInvite?.name || 'este contato'}
            </DialogDescription>
          </DialogHeader>

          {resendModalInvite && (
            <Tabs defaultValue="share" className="w-full">
              <TabsList className="grid w-full grid-cols-2 h-auto p-1 mb-4">
                <TabsTrigger 
                  value="share" 
                  className="flex flex-col items-center gap-1.5 py-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 dark:data-[state=active]:bg-blue-900/30 dark:data-[state=active]:text-blue-400"
                >
                  <Link2 className="w-5 h-5" />
                  <span className="text-xs font-medium">Compartilhar link</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="email" 
                  className="flex flex-col items-center gap-1.5 py-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 dark:data-[state=active]:bg-blue-900/30 dark:data-[state=active]:text-blue-400"
                  disabled={resendModalInvite.contact_type !== 'email'}
                >
                  <Mail className="w-5 h-5" />
                  <span className="text-xs font-medium">Enviar e-mail</span>
                </TabsTrigger>
              </TabsList>

              {/* Tab 1: Share Link */}
              <TabsContent value="share" className="space-y-4 mt-0">
                {/* Contact info */}
                <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      resendModalInvite.contact_type === 'email' 
                        ? 'bg-blue-100 dark:bg-blue-900/30' 
                        : 'bg-green-100 dark:bg-green-900/30'
                    }`}>
                      {resendModalInvite.contact_type === 'email' ? (
                        <Mail className="w-5 h-5 text-blue-500" />
                      ) : (
                        <Phone className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">
                        {resendModalInvite.name || 'Sem nome'}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {resendModalInvite.contact}
                      </p>
                    </div>
                    <Badge variant="outline" className="ml-auto">
                      {resendModalInvite.send_count} envio{resendModalInvite.send_count !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                </div>

                {/* Link Input */}
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600 dark:text-slate-400">Link de convite</Label>
                  <div className="flex gap-2">
                    <Input
                      value={`${PUBLIC_URL}/dashboard?join=${trip?.invite_code || ''}`}
                      readOnly
                      className="flex-1 bg-slate-50 dark:bg-slate-800 text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopyInviteLink}
                      className="shrink-0"
                    >
                      {copied ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Invite Code */}
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600 dark:text-slate-400">Código de convite</Label>
                  <div className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <span className="flex-1 text-xl font-mono font-bold tracking-widest text-slate-900 dark:text-white text-center">
                      {trip?.invite_code || ''}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCopyInviteCode}
                      className="shrink-0"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Share buttons */}
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <Button
                    variant="outline"
                    className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 dark:text-green-400 dark:border-green-800"
                    onClick={() => window.open(getWhatsAppShareLink(resendModalInvite), '_blank')}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCopyInviteLink}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar link
                  </Button>
                </div>
              </TabsContent>

              {/* Tab 2: Send Email */}
              <TabsContent value="email" className="space-y-4 mt-0">
                {/* Contact info pre-filled */}
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600 dark:text-slate-400">Nome</Label>
                    <Input
                      value={resendModalInvite.name || ''}
                      readOnly
                      className="bg-slate-50 dark:bg-slate-800"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600 dark:text-slate-400">E-mail</Label>
                    <Input
                      value={resendModalInvite.contact}
                      readOnly
                      className="bg-slate-50 dark:bg-slate-800"
                    />
                  </div>
                </div>

                {/* Cooldown warning */}
                {!canResendEmail(resendModalInvite) && (
                  <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                    <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        Aguarde {getEmailCooldownMinutes(resendModalInvite)} minutos para reenviar por e-mail
                      </span>
                    </div>
                    <p className="text-xs text-orange-600 dark:text-orange-500 mt-1 ml-6">
                      Use a aba &ldquo;Compartilhar link&rdquo; para enviar via WhatsApp
                    </p>
                  </div>
                )}

                {/* Send Button */}
                <Button
                  className="w-full bg-blue-500 hover:bg-blue-600"
                  onClick={handleSendEmailFromModal}
                  disabled={!canResendEmail(resendModalInvite) || resendingInvite === resendModalInvite.contact}
                >
                  {resendingInvite === resendModalInvite.contact ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Reenviar e-mail
                    </>
                  )}
                </Button>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PendingMemberCard({ member, index, onApprove, onReject, isLoading, t, dateLocale }) {
  const requestedAt = member.requested_at 
    ? formatDistanceToNow(new Date(member.requested_at), { addSuffix: true, locale: dateLocale })
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card className="border-yellow-200 dark:border-yellow-900/50 bg-yellow-50 dark:bg-yellow-900/10">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Avatar className="w-12 h-12">
              <AvatarFallback className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 text-xl">
                {member.user_avatar || '😊'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-slate-900 dark:text-white truncate">
                {member.user_name}
              </h4>
              <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{member.user_email}</p>
              
              <div className="flex items-center gap-1.5 mt-1 text-xs text-yellow-600 dark:text-yellow-400">
                <Clock className="w-3 h-3" />
                <span>{t('members.requestedAt')} {requestedAt || t('common.recently')}</span>
              </div>
              
              {/* Action buttons */}
              <div className="flex items-center gap-2 mt-3">
                <Button
                  size="sm"
                  onClick={onApprove}
                  disabled={isLoading}
                  className="flex-1 h-8 bg-green-600 hover:bg-green-700 text-white"
                  data-testid={`approve-member-${member.id}`}
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      {t('common.approve')}
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onReject}
                  disabled={isLoading}
                  className="flex-1 h-8 border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-900/20"
                  data-testid={`reject-member-${member.id}`}
                >
                  <X className="w-4 h-4 mr-1" />
                  {t('common.reject')}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function MemberCard({ member, index, isCurrentUser, isOrganizer, onViewDetails, roleConfig, statusConfig, t }) {
  const role = roleConfig[member.role] || roleConfig.traveler;
  const status = statusConfig[member.status] || statusConfig.confirmed;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card className={`border-slate-100 dark:border-slate-700 dark:bg-slate-800/50 ${isCurrentUser ? 'ring-2 ring-orange-200 dark:ring-orange-500/30' : ''}`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Avatar className="w-12 h-12">
              <AvatarFallback className="bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-xl">
                {member.user_avatar || '😊'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-slate-900 dark:text-white truncate">
                  {member.user_name}
                </h4>
                {isCurrentUser && (
                  <Badge variant="outline" className="text-xs">{t('common.you')}</Badge>
                )}
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{member.user_email}</p>
              
              <div className="flex items-center justify-between gap-2 mt-2">
                <div className="flex items-center gap-2">
                  <Badge className={role.color}>{role.label}</Badge>
                  <div className={`flex items-center gap-1 text-xs ${status.color}`}>
                    <StatusIcon className="w-3 h-3" />
                    <span>{status.label}</span>
                  </div>
                </div>
                
                {(isOrganizer || isCurrentUser) && member.status === 'confirmed' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onViewDetails}
                    className="h-7 px-2 text-slate-500 hover:text-orange-500"
                    data-testid={`view-member-details-${member.user_id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
